import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { OrgReportsApi, type OrgStudentSummaryReportRow } from '@/services/org-reports.api';
import { OrgPortalApi } from '@/services/org.api';
import type { ExportFormat } from '@/services/school.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Input, Select } from '@/components/ui/Input';
import { DataTable, useDataTable } from '@/components/data-table';
import { FileSpreadsheet, FileText, Loader2, Search } from 'lucide-react';

const columnHelper = createColumnHelper<OrgStudentSummaryReportRow>();

/**
 * Student Summary Report — org tier, read-only. Per-school x per-class
 * active-student-count matrix, matching the real `state-reports.
 * school-class-wise-report` shape. Column axis is the real union of
 * distinct class names across the schools in scope (capped at 40),
 * not real legacy's own hardcoded "first 7 classes of school id=2" hack.
 */
export function OrgStudentSummaryReportPage() {
  const [schoolId, setSchoolId] = useState('');
  const [stateId, setStateId] = useState<number | ''>('');
  const [status, setStatus] = useState<'active' | 'inactive' | 'all'>('active');
  const [showDeleted, setShowDeleted] = useState(false);
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState<ExportFormat | null>(null);
  const dt = useDataTable({ defaultLimit: 20 });

  const { data: schools = [] } = useQuery({
    queryKey: ['schools-for-org-student-summary'],
    queryFn: async () => (await OrgPortalApi.listSchools({ limit: 100 })).items,
  });

  const { data: states = [] } = useQuery({
    queryKey: ['org-report-states'],
    queryFn: () => OrgReportsApi.listStates(),
  });

  const filterParams = {
    schoolId: schoolId || undefined,
    stateId: stateId === '' ? undefined : stateId,
    status,
    showDeleted: showDeleted || undefined,
    search: search.trim() || undefined,
  };

  const { data, isFetching } = useQuery({
    queryKey: ['org-student-summary', filterParams],
    queryFn: () => OrgReportsApi.studentSummaryReport(filterParams),
  });

  const allRows = data?.rows ?? [];
  const classNames = data?.classNames ?? [];

  // Bounded, pre-aggregated rollup (one row per school in scope) —
  // paginate client-side over the already-loaded full array, same pattern
  // as OrgSchoolFeesSummaryPage, rather than adding server-side pagination
  // for a result set this small.
  const total = allRows.length;
  const totalPages = Math.max(1, Math.ceil(total / dt.limit));
  const page = Math.min(dt.page, totalPages);
  const pageRows = allRows.slice((page - 1) * dt.limit, page * dt.limit);

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'no',
        header: 'No.',
        cell: (info) => <span className="text-slate-400">{(page - 1) * dt.limit + info.row.index + 1}</span>,
      }),
      columnHelper.accessor('schoolName', { id: 'schoolName', header: 'School', cell: (info) => <span className="font-medium text-slate-800">{info.getValue()}</span> }),
      ...classNames.map((c) =>
        columnHelper.display({
          id: `class-${c}`,
          header: c,
          cell: (info) => <span className="text-slate-600">{info.row.original.cells?.[c] || ''}</span>,
        }),
      ),
      columnHelper.accessor('total', { id: 'total', header: 'Total', cell: (info) => <span className="font-semibold text-slate-800">{info.getValue()}</span> }),
    ],
    [page, dt.limit, classNames],
  );

  const runExport = async (format: ExportFormat) => {
    if (busy) return;
    setBusy(format);
    try {
      await OrgReportsApi.exportStudentSummaryReport(format, filterParams);
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <PageHeader
        title="Student Summary Report"
        description="Active student counts per class, per school, across your organization."
        actions={
          <div className="inline-flex overflow-hidden rounded-md border border-slate-200">
            <button
              type="button"
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50"
              onClick={() => runExport('xlsx')}
              disabled={!!busy}
              title="Export to Excel"
            >
              {busy === 'xlsx' ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 text-green-600" />}
              Excel
            </button>
            <div className="w-px bg-slate-200" />
            <button
              type="button"
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50"
              onClick={() => runExport('pdf')}
              disabled={!!busy}
              title="Export to PDF"
            >
              {busy === 'pdf' ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4 text-red-600" />}
              PDF
            </button>
          </div>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by school name, email, phone…"
            className="w-64 pl-9"
          />
        </div>
        <Select className="!w-56" value={schoolId} onChange={(e) => setSchoolId(e.target.value)}>
          <option value="">All schools</option>
          {schools.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Select>
        <Select
          className="!w-48"
          value={stateId}
          onChange={(e) => setStateId(e.target.value === '' ? '' : Number(e.target.value))}
        >
          <option value="">All states</option>
          {states.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Select>
        <Select className="!w-36" value={status} onChange={(e) => setStatus(e.target.value as 'active' | 'inactive' | 'all')}>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="all">All</option>
        </Select>
        <label className="flex items-center gap-1.5 text-sm text-slate-600">
          <input type="checkbox" checked={showDeleted} onChange={(e) => setShowDeleted(e.target.checked)} />
          Show Deleted
        </label>
      </div>

      {data && (
        <div className="mb-4 flex flex-wrap gap-3">
          <div className="rounded-lg border border-slate-200 bg-white px-4 py-3 shadow-sm">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-400">Total Students</div>
            <div className="text-2xl font-bold text-slate-800">{data.grandTotal}</div>
          </div>
        </div>
      )}

      <DataTable
        columns={columns}
        result={{ items: pageRows, total, page, limit: dt.limit, totalPages }}
        state={dt}
        loading={isFetching}
        emptyMessage="No schools found."
      />
    </>
  );
}
